import React, { useState, useEffect } from 'react';
import { PEN_COLORS } from '../utils/whiteboardUtils';
import { STICKER_TYPES } from '../hooks/useWhiteboardSticker';
import {
  IconCursor, IconT, IconShape, IconPen, IconHighlighter, IconEraser,
  IconSticker, IconTrash, IconRect, IconCircle, IconTriangle,
  IconArrow, IconLine, IconMessage,
} from './WhiteboardIcons';

const ACCENT = '#ee5a24';

const btnBase = (active) => ({
  background: active ? 'rgba(238,90,36,0.10)' : 'none',
  border: active ? `2px solid rgba(238,90,36,0.25)` : '2px solid transparent',
  borderRadius: '50%',
  padding: 10,
  margin: 0,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'background 0.15s, transform 0.15s, border-color 0.15s',
  boxShadow: 'none',
  width: 44,
  height: 44,
  fontSize: 22,
  color: active ? ACCENT : '#555',
  outline: 'none',
  WebkitTapHighlightColor: 'transparent',
});

const hoverOn = (el) => {
  el.style.background = 'rgba(0,0,0,0.06)';
  el.style.transform = 'scale(1.1)';
};
const hoverOff = (el, active) => {
  el.style.background = active ? 'rgba(238,90,36,0.10)' : 'none';
  el.style.transform = 'scale(1)';
};

export default function WhiteboardToolbar({
  tool, setTool, setPenType,
  penColor, setPenColor,
  penThickness, setPenThickness,
  stickerPopup, setStickerPopup,
  pan, setShapes,
  isViewOnly, isStudent,
  handleStickerClick, handleEraserClick,
}) {
  const [penPopup, setPenPopup] = useState(false);
  const [highlighterPopup, setHighlighterPopup] = useState(false);

  const TOOLBAR = [
    { key: 'select',      icon: <IconCursor />,     label: 'Select' },
    { key: 'pen',         icon: <IconPen />,        label: 'Pen' },
    { key: 'highlighter', icon: <IconHighlighter />, label: 'Highlighter' },
    { key: 'eraser',      icon: <IconEraser />,     label: 'Eraser' },
    { key: 'shape',       icon: <IconShape />,      label: 'Shape' },
    { key: 'sticker',     icon: <IconSticker />,    label: 'Sticker' },
    { key: 'text',        icon: <IconT />,          label: 'Text' },
    { key: 'clear',       icon: <IconTrash />,      label: 'Clear All' },
  ];

  const handlePenClick = () => {
    setTool('pen');
    setPenType('pen');
    setPenPopup(true);
  };

  useEffect(() => {
    if (!penPopup) return;
    const close = e => {
      if (!e.target.closest('.pen-popup') && !e.target.closest('.pen-btn')) setPenPopup(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [penPopup]);

  const handleHighlighterClick = () => {
    if (tool === 'highlighter' && highlighterPopup) {
      setHighlighterPopup(false);
    } else {
      setTool('highlighter');
      setPenType('highlighter');
      setHighlighterPopup(true);
    }
  };

  useEffect(() => {
    if (!highlighterPopup) return;
    const close = e => {
      if (!e.target.closest('.highlighter-popup') && !e.target.closest('.highlighter-btn')) setHighlighterPopup(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [highlighterPopup]);

  const THICKNESS_PRESETS = [2, 4, 6, 10, 16];

  const popupStyle = {
    position: 'absolute',
    left: 60,
    top: 0,
    background: '#fff',
    borderRadius: 18,
    boxShadow: '0 12px 40px rgba(0,0,0,0.14), 0 2px 8px rgba(0,0,0,0.06)',
    padding: '16px',
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
    zIndex: 100,
    border: '1px solid rgba(0,0,0,0.06)',
    width: 212,
  };

  const strokePreview = (
    <div style={{
      background: '#f7f8fa',
      borderRadius: 12,
      padding: '10px 14px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}>
      <svg width="160" height="28" viewBox="0 0 160 28">
        <path
          d="M8 20 C 30 4, 50 24, 80 14 S 130 4, 152 14"
          fill="none"
          stroke={penColor}
          strokeWidth={Math.min(penThickness, 14)}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </div>
  );

  const thicknessSection = (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}>
      {THICKNESS_PRESETS.map(t => {
        const active = penThickness === t;
        return (
          <button
            key={t}
            onClick={() => setPenThickness(t)}
            title={`${t}px`}
            style={{
              flex: 1,
              height: 36,
              borderRadius: 10,
              border: active ? `2px solid rgba(238,90,36,0.35)` : '2px solid transparent',
              background: active ? 'rgba(238,90,36,0.07)' : 'rgba(0,0,0,0.04)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              outline: 'none',
              WebkitTapHighlightColor: 'transparent',
              transition: 'background 0.12s, border-color 0.12s, transform 0.12s',
            }}
            onMouseEnter={e => { if (!active) { e.currentTarget.style.background = 'rgba(0,0,0,0.08)'; e.currentTarget.style.transform = 'scale(1.08)'; } }}
            onMouseLeave={e => { e.currentTarget.style.background = active ? 'rgba(238,90,36,0.07)' : 'rgba(0,0,0,0.04)'; e.currentTarget.style.transform = 'scale(1)'; }}
          >
            <div style={{
              width: '60%',
              height: Math.min(t, 10),
              borderRadius: 99,
              background: active ? ACCENT : '#999',
              transition: 'background 0.12s',
            }} />
          </button>
        );
      })}
    </div>
  );

  const colorSection = (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 8 }}>
      {PEN_COLORS.map(c => {
        const active = penColor === c;
        return (
          <button
            key={c}
            onClick={() => setPenColor(c)}
            style={{
              width: 28,
              height: 28,
              borderRadius: '50%',
              background: c,
              border: active ? `3px solid ${ACCENT}` : '2.5px solid rgba(0,0,0,0.08)',
              cursor: 'pointer',
              outline: 'none',
              WebkitTapHighlightColor: 'transparent',
              boxShadow: active ? `0 0 0 2px rgba(238,90,36,0.20)` : 'none',
              transition: 'transform 0.12s, box-shadow 0.12s',
              padding: 0,
            }}
            onMouseEnter={e => { if (!active) e.currentTarget.style.transform = 'scale(1.2)'; }}
            onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; }}
          />
        );
      })}
    </div>
  );

  return (
    <div
      style={{
        position: 'absolute',
        left: 24,
        top: 55,
        background: '#fff',
        borderRadius: 22,
        boxShadow: '0 8px 32px 0 rgba(88,84,84,0.13)',
        padding: '18px 10px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 8,
        zIndex: 2010,
        minHeight: 0,
      }}
    >
      {!isViewOnly && TOOLBAR.map(btn => {
        const active = tool === btn.key;

        if (btn.key === 'clear' && isStudent) return null;
        if (btn.key === 'clear') return (
          <button
            key={btn.key}
            className="whiteboard-toolbar-btn"
            style={{
              background: 'none',
              border: '2px solid transparent',
              borderRadius: '50%',
              padding: 10, margin: 0, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'background 0.15s, transform 0.15s',
              width: 44, height: 44, fontSize: 22,
              color: '#e11d48',
              outline: 'none',
              WebkitTapHighlightColor: 'transparent',
            }}
            title={btn.label}
            onClick={() => setShapes([])}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(225,29,72,0.08)'; e.currentTarget.style.transform = 'scale(1.1)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'none'; e.currentTarget.style.transform = 'scale(1)'; }}
          >
            {btn.icon}
          </button>
        );

        if (btn.key === 'pen') return (
          <div key={btn.key} style={{ position: 'relative' }}>
            <button
              className="whiteboard-toolbar-btn pen-btn"
              style={btnBase(active)}
              title={btn.label}
              onClick={handlePenClick}
              onMouseEnter={e => { if (!active) hoverOn(e.currentTarget); }}
              onMouseLeave={e => hoverOff(e.currentTarget, active)}
            >
              {btn.icon}
            </button>
            {penPopup && active && (
              <div className="pen-popup" style={popupStyle}>
                {strokePreview}
                {thicknessSection}
                <div style={{ height: 1, background: 'rgba(0,0,0,0.06)' }} />
                {colorSection}
              </div>
            )}
          </div>
        );

        if (btn.key === 'highlighter') return (
          <div key={btn.key} style={{ position: 'relative' }}>
            <button
              className="whiteboard-toolbar-btn highlighter-btn"
              style={btnBase(active)}
              title={btn.label}
              onClick={handleHighlighterClick}
              onMouseEnter={e => { if (!active) hoverOn(e.currentTarget); }}
              onMouseLeave={e => hoverOff(e.currentTarget, active)}
            >
              {btn.icon}
            </button>
            {highlighterPopup && active && (
              <div className="highlighter-popup" style={popupStyle}>
                {strokePreview}
                {thicknessSection}
                <div style={{ height: 1, background: 'rgba(0,0,0,0.06)' }} />
                {colorSection}
              </div>
            )}
          </div>
        );

        if (btn.key === 'eraser') return (
          <div key={btn.key} style={{ position: 'relative' }}>
            <button
              className="whiteboard-toolbar-btn eraser-btn"
              style={btnBase(active)}
              title={btn.label}
              onClick={handleEraserClick}
              onMouseEnter={e => { if (!active) hoverOn(e.currentTarget); }}
              onMouseLeave={e => hoverOff(e.currentTarget, active)}
            >
              {btn.icon}
            </button>
          </div>
        );

        if (btn.key === 'shape') {
          const SHAPE_TOOLS = ['shape', 'circle', 'triangle', 'arrow', 'line', 'message'];
          const shapeActive = SHAPE_TOOLS.includes(tool);
          return (
            <div key={btn.key} style={{ position: 'relative' }}>
              <button
                className="whiteboard-toolbar-btn shape-btn"
                style={btnBase(shapeActive)}
                title={btn.label}
                onClick={() => setTool('shape')}
                onMouseEnter={e => { if (!shapeActive) hoverOn(e.currentTarget); }}
                onMouseLeave={e => hoverOff(e.currentTarget, shapeActive)}
              >
                {btn.icon}
              </button>
              {shapeActive && (
                <div className="shape-popup" style={{ ...popupStyle, width: 'auto', padding: 10 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6 }}>
                    {[
                      { key: 'shape',    icon: <IconRect />,     label: 'Rect' },
                      { key: 'circle',   icon: <IconCircle />,   label: 'Circle' },
                      { key: 'triangle', icon: <IconTriangle />, label: 'Triangle' },
                      { key: 'arrow',    icon: <IconArrow />,    label: 'Arrow' },
                      { key: 'line',     icon: <IconLine />,     label: 'Line' },
                      { key: 'message',  icon: <IconMessage />,  label: 'Message' },
                    ].map(s => {
                      const sActive = tool === s.key;
                      return (
                        <button
                          key={s.key}
                          onClick={() => setTool(s.key)}
                          title={s.label}
                          style={{
                            border: sActive ? `2px solid rgba(238,90,36,0.35)` : '2px solid #e8e8e8',
                            background: sActive ? 'rgba(238,90,36,0.08)' : '#fafafa',
                            borderRadius: 10,
                            padding: 9,
                            cursor: 'pointer',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            color: sActive ? ACCENT : '#555',
                            transition: 'background 0.13s, transform 0.13s',
                            outline: 'none',
                            WebkitTapHighlightColor: 'transparent',
                          }}
                          onMouseEnter={e => { if (!sActive) { e.currentTarget.style.background = 'rgba(0,0,0,0.06)'; e.currentTarget.style.transform = 'scale(1.08)'; } }}
                          onMouseLeave={e => { e.currentTarget.style.background = sActive ? 'rgba(238,90,36,0.08)' : '#fafafa'; e.currentTarget.style.transform = 'scale(1)'; }}
                        >
                          {s.icon}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          );
        }

        if (btn.key === 'sticker') return (
          <div key={btn.key} style={{ position: 'relative' }}>
            <button
              className="whiteboard-toolbar-btn sticker-btn"
              style={btnBase(active)}
              title={btn.label}
              onClick={handleStickerClick}
              onMouseEnter={e => { if (!active) hoverOn(e.currentTarget); }}
              onMouseLeave={e => hoverOff(e.currentTarget, active)}
            >
              {btn.icon}
            </button>
            {stickerPopup && active && (
              <div className="sticker-popup" style={{
                position: 'absolute', left: 60, top: 0,
                background: '#f8fafc',
                borderRadius: 16,
                boxShadow: '0 8px 24px rgba(0,0,0,0.10)',
                padding: '14px', display: 'flex', flexDirection: 'row',
                gap: 12, zIndex: 100, border: '1px solid #e8eaed',
                minWidth: 280, alignItems: 'flex-end',
              }}>
                {STICKER_TYPES.map((s, i) => (
                  <button
                    key={s.key}
                    style={{
                      background: s.color,
                      border: `2px solid ${s.border}`,
                      color: s.textColor,
                      borderRadius: '14px 14px 24px 14px',
                      width: 58, height: 58,
                      fontWeight: 700, fontSize: 15,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      cursor: 'pointer',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.10)',
                      transform: `rotate(${(i - 1) * 3}deg) skew(-1.5deg,0deg)`,
                      position: 'relative',
                      transition: 'transform 0.15s, box-shadow 0.15s',
                      outline: 'none',
                      WebkitTapHighlightColor: 'transparent',
                      overflow: 'visible',
                    }}
                    onMouseEnter={e => {
                      e.currentTarget.style.transform = `rotate(${(i - 1) * 3}deg) scale(1.08) skew(-1.5deg,0deg)`;
                      e.currentTarget.style.boxShadow = '0 8px 20px rgba(0,0,0,0.15)';
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.transform = `rotate(${(i - 1) * 3}deg) skew(-1.5deg,0deg)`;
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.10)';
                    }}
                    onClick={() => {
                      const x = (window.innerWidth / 2 - pan.x) / pan.scale;
                      const y = (window.innerHeight / 2 - pan.y) / pan.scale;
                      setShapes(prev => [...prev, {
                        id: Date.now(), type: 'sticker', stickerType: s.key,
                        x, y, w: 160, h: 100,
                        color: s.color, border: s.border, textColor: s.textColor,
                        text: s.label,
                        styles: {
                          fontWeight: '600', fontSize: '18px', textAlign: 'center',
                          fontFamily: 'Arial, sans-serif', fontStyle: 'normal',
                          textDecoration: 'none', color: s.textColor,
                        },
                      }]);
                      setStickerPopup(false);
                    }}
                  >
                    <span style={{
                      position: 'absolute', right: 0, bottom: 0,
                      width: 18, height: 18,
                      background: 'linear-gradient(135deg,#fffbe7 60%,#f8fafc 100%)',
                      borderRadius: '0 0 14px 0',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.06)',
                      border: '1.5px solid #e2e8f0',
                      transform: 'rotate(7deg) translate(6px,6px)',
                      zIndex: 1, opacity: 0.7,
                    }} />
                    <span style={{ position: 'relative', zIndex: 2 }}>{s.label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        );

        // select, text — generic
        return (
          <button
            key={btn.key}
            className="whiteboard-toolbar-btn"
            style={btnBase(active)}
            title={btn.label}
            onClick={() => setTool(btn.key)}
            onMouseEnter={e => { if (!active) hoverOn(e.currentTarget); }}
            onMouseLeave={e => hoverOff(e.currentTarget, active)}
          >
            {btn.icon}
          </button>
        );
      })}
    </div>
  );
}
